import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import TextBlock from '@/components/TextBlock'
import Carousel from '@/components/Carousel'
import RevealGrid from '@/components/RevealGrid'
import ProductItem from '@/components/ProductItem'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { cutExcerpt, insertBrAfterFirstSpace } from '@/utils/strings'
import style from './style'

const useStyles = createUseStyles(style)

const Art = ({
  cpt: cptFromProps,
  stub,
}) => {
  const { getImageUrl, setPageAnimationReady } = useContext(GeneralContext)
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  const [archive, setArchive] = useState([])
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const gallery = useRef([])

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey, pages, listCpt, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
    pages: state.page.pages,
    mediaQueryKey: state.mediaQuery.key,
    listCpt: state.page.archive[cptFromProps],
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const getCPT = useCallback((cpt, index) => dispatch(actions.page.getListCPT(cpt, index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub, 'pages')
    preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
    getCPT(cptFromProps)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = () => {
    if (page.project_gallery_id) {
      const gal = page.project_gallery_id.split(',')
      const imgSize = mediaQueryKey > 2 ? 'full' : 'large'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub, 'pages')
          const ratio = height / width
          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      gallery.current = galleryItems[0] ? galleryItems : []
    }
  }

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (archive.length > 0 && Object.keys(page).length > 0) {
      preloadMedia()
      setGallery()
    }
  }, [archive, page])

  /*------------------------------
  Map Categories to Product
  ------------------------------*/
  useEffect(() => {
    if (listCpt !== undefined) {
      const listMapped = listCpt.map((cat) => {
        const post = pages[cat]
        return ({
          id: post.id,
          title: post.title.rendered,
          denomination: '',
          link: post.categories.includes(14) ? post.external_link : post.link,
          image: getImageUrl(post.featured_media, 'large', cat, 'pages'),
          categories: post.categories,
        })
      })
      setArchive(listMapped)
    }
  }, [listCpt])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (
      !load
      && archive.length === 0
      && Object.keys(page).length === 0
    ) {
      initialize()
    }
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Cover Home
  ------------------------------*/
  const renderCoverHome = () => {
    return (page.featured_media && page.title.rendered) && (
      <Hero
        aboveTheFold={true}
        img={getImageUrl(page.featured_media, 'full', stub, 'pages')}
        text={cutExcerpt(page.content.rendered)}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        fontSize="md"
        color="light"
      />
    )
  }

  /*------------------------------
  Render Intro
  ------------------------------*/
  const renderIntroText = () => {
    return (
      <div className={`${classes.wrapper} ${classes.intro}`}>
        <TextBlock
          title={page.banner_single_title}
          text={page.banner_single_text}
        />
      </div>
    )
  }

  /*------------------------------
  Render Related Projects
  ------------------------------*/
  const renderProjects = () => {
    return page.related_projects.length > 0 && page.related_projects.map((item) => (
      <Hero
        key={item.id}
        borderTitle
        fontSize="sm"
        img={(mediaQueryKey <= 1 && item.image_mobile) ? item.image_mobile : item.image}
        link={item.link}
        pretitle={strings.projects_label}
        text={cutExcerpt(item.text)}
        title={item.title}
      />
    ))
  }

  /*------------------------------
  Render Additional Text
  ------------------------------*/
  const renderAdditionalText = () => {
    return (
      <div className={`${classes.wrapper} ${classes.intro}`}>
        <TextBlock
          className={classes.additionalText}
          title={page.banner_single_title2}
          text={page.banner_single_text2}
        />
      </div>
    )
  }

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGallery = () => {
    return gallery.current.length > 1 && (
      <div className={classes.gallery}>
        <Carousel
          layout="white"
          imagesList={gallery.current}
        />
      </div>
    )
  }

  /*------------------------------
  Render List
  ------------------------------*/
  const renderList = () => {
    const newsArt = archive.filter((news) => news.categories.includes(12)) // Include only Art News
    return newsArt.length > 0 && (
      <RevealGrid
        cols={1}
        className={classes.listView}
      >
          {newsArt.map((item) => (
            <ProductItem
              key={item.id}
              item={item}
              layout="artnews"
              externalLink={item.categories.includes(14)}
            />
          ))}
      </RevealGrid>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverHome()}
        {renderIntroText()}
        {renderProjects()}
        {renderAdditionalText()}
        {renderGallery()}
        {renderList()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArt ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Art
